import React, { useRef } from 'react';
import {
  Box,
  Collapse,
  CircularProgress,
  IconButton,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  TextField,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { HelpArticle, HelpModule, HelpSubModule } from '../../types/help';
import { HELP_DOC_THEME } from './helpTheme';

const INDENT_SUBMODULE = 12;
const CHILD_BLOCK_INDENT = 12;
/** Match Collections left-nav type (Inter + folder/request sizes). */
const HELP_NAV_FONT_FAMILY =
  "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica Neue', sans-serif";
const HELP_NAV_MODULE_FONT_SIZE = { xs: '0.9375rem', md: '0.875rem', xl: '0.9375rem' } as const;
const HELP_NAV_SUBMODULE_FONT_SIZE = { xs: '0.875rem', md: '0.8125rem', xl: '0.875rem' } as const;

export interface HelpSidebarProps {
  modules: HelpModule[];
  subModulesByModuleId: Record<string, HelpSubModule[]>;
  articlesBySubModuleId: Record<string, HelpArticle[]>;
  expandedModuleIds: Record<string, boolean>;
  expandedSubModuleIds: Record<string, boolean>;
  loadingSubModuleModuleIds: Record<string, boolean>;
  loadingArticlesSubModuleIds: Record<string, boolean>;
  selectedModuleId: string;
  selectedSubModuleId: string;
  selectedArticleId: string;
  loadingModules: boolean;
  onModuleNavigate: (moduleId: string) => void;
  onToggleModuleExpand: (moduleId: string) => void;
  onToggleSubModuleExpand: (subModuleId: string) => void;
  onSubModuleNavigate: (moduleId: string, subModuleId: string) => void;
  onArticleNavigate: (moduleId: string, subModuleId: string, articleId: string) => void;
  onOpenSearch: () => void;
  sidebarSearchQuery: string;
  onSidebarSearchChange: (q: string) => void;
}

const HelpSidebar: React.FC<HelpSidebarProps> = ({
  modules,
  subModulesByModuleId,
  articlesBySubModuleId,
  expandedModuleIds,
  expandedSubModuleIds,
  loadingSubModuleModuleIds,
  loadingArticlesSubModuleIds,
  selectedModuleId,
  selectedSubModuleId,
  selectedArticleId,
  loadingModules,
  onModuleNavigate,
  onToggleModuleExpand,
  onToggleSubModuleExpand,
  onSubModuleNavigate,
  onArticleNavigate,
  onOpenSearch,
  sidebarSearchQuery,
  onSidebarSearchChange,
}) => {
  const searchInputRef = useRef<HTMLInputElement>(null);

  const subModuleRowSx = (active: boolean) => ({
    pl: `${INDENT_SUBMODULE}px`,
    pr: '6px',
    py: '6px',
    minHeight: 40,
    borderRadius: 0,
    position: 'relative',
    bgcolor: active && !selectedArticleId ? 'rgba(0,0,0,0.04)' : 'transparent',
    '&::before': {
      content: '""',
      position: 'absolute',
      left: 0,
      top: 0,
      bottom: 0,
      width: '3px',
      bgcolor: HELP_DOC_THEME.accent,
      opacity: active && !selectedArticleId ? 1 : 0,
    },
    '&:hover': { bgcolor: 'rgba(0,0,0,0.03)' },
  });

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        fontFamily: HELP_NAV_FONT_FAMILY,
        lineHeight: 1.35,
        color: HELP_DOC_THEME.text,
      }}
    >
      <Box sx={{ px: 1.25, pt: 1.25, pb: 1, borderBottom: `1px solid ${HELP_DOC_THEME.border}` }}>
        <Typography sx={{ color: HELP_DOC_THEME.textMuted, letterSpacing: 0.6, fontWeight: 700, fontSize: '0.6875rem' }}>
          Learning Center
        </Typography>
        <Typography
          component="div"
          sx={{
            fontWeight: 700,
            mt: '2px',
            mb: 1,
            color: HELP_DOC_THEME.text,
            fontSize: '0.9375rem',
            lineHeight: 1.25,
          }}
        >
          Docs
        </Typography>

        <TextField
          inputRef={searchInputRef}
          size="small"
          fullWidth
          placeholder="Search"
          value={sidebarSearchQuery}
          onChange={(e) => onSidebarSearchChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              onOpenSearch();
            }
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: HELP_DOC_THEME.textMuted, fontSize: 18 }} />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end" sx={{ gap: 0.5, mr: 0.25 }}>
                <AutoAwesomeIcon sx={{ color: HELP_DOC_THEME.accent, fontSize: 16, opacity: 0.9 }} titleAccess="AI-assisted search in dialog (Ctrl+K)" />
              </InputAdornment>
            ),
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              fontSize: '0.875rem',
              bgcolor: HELP_DOC_THEME.pageBg,
              borderRadius: '10px',
              height: 40,
            },
            '& .MuiOutlinedInput-notchedOutline': { borderColor: HELP_DOC_THEME.border },
            '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': { borderColor: HELP_DOC_THEME.textMuted },
          }}
        />
      </Box>

      <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', py: '8px' }}>
        {loadingModules ? (
          <Box sx={{ py: 3, display: 'flex', justifyContent: 'center' }}>
            <CircularProgress size={22} />
          </Box>
        ) : (
          <List dense disablePadding sx={{ px: 0 }}>
            {modules.map((module) => {
              const modOpen = !!expandedModuleIds[module.id];
              const subs = subModulesByModuleId[module.id] ?? [];
              const loadingMod = !!loadingSubModuleModuleIds[module.id];
              const showModuleChevron = true;
              const moduleActive = selectedModuleId === module.id && !selectedSubModuleId;

              return (
                <Box key={module.id}>
                  <ListItemButton
                    onClick={() => onModuleNavigate(module.id)}
                    sx={{
                      py: '8px',
                      pl: '10px',
                      pr: '6px',
                      minHeight: 36,
                      borderRadius: 0,
                      position: 'relative',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      bgcolor: moduleActive ? 'rgba(0,0,0,0.035)' : 'transparent',
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        left: 0,
                        top: 0,
                        bottom: 0,
                        width: '3px',
                        bgcolor: HELP_DOC_THEME.accent,
                        opacity: moduleActive ? 1 : 0,
                      },
                    }}
                  >
                    <ListItemText
                      primary={module.name}
                      primaryTypographyProps={{
                        fontWeight: moduleActive || modOpen ? 600 : 500,
                        fontSize: HELP_NAV_MODULE_FONT_SIZE,
                        lineHeight: 1.35,
                        color: HELP_DOC_THEME.text,
                        fontFamily: HELP_NAV_FONT_FAMILY,
                        noWrap: true,
                        sx: { overflow: 'hidden', textOverflow: 'ellipsis' },
                      }}
                    />
                    {showModuleChevron && (
                      <IconButton
                        size="small"
                        aria-expanded={modOpen}
                        aria-label={modOpen ? 'Collapse section' : 'Expand section'}
                        onClick={(e) => {
                          e.stopPropagation();
                          onToggleModuleExpand(module.id);
                        }}
                        sx={{ color: HELP_DOC_THEME.textMuted, p: '2px' }}
                      >
                        <ChevronRightIcon
                          sx={{
                            fontSize: 18,
                            transform: modOpen ? 'rotate(90deg)' : 'rotate(0deg)',
                            transition: 'transform 0.15s ease',
                          }}
                        />
                      </IconButton>
                    )}
                  </ListItemButton>

                  <Collapse in={modOpen} timeout={200} unmountOnExit>
                    <Box sx={{ pb: '4px', ml: `${CHILD_BLOCK_INDENT}px` }}>
                      {loadingMod ? (
                        <Box sx={{ py: 1, pl: 4, display: 'flex', justifyContent: 'center' }}>
                          <CircularProgress size={16} />
                        </Box>
                      ) : (
                        <List dense disablePadding>
                          {subs.map((sub) => {
                            const subActive =
                              sub.id === selectedSubModuleId &&
                              selectedModuleId === module.id &&
                              !!selectedArticleId;

                            return (
                              <Box key={sub.id}>
                                <ListItemButton
                                  onClick={() => onSubModuleNavigate(module.id, sub.id)}
                                  sx={{
                                    ...subModuleRowSx(subActive),
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                  }}
                                >
                                  <ListItemText
                                    primary={sub.name}
                                    primaryTypographyProps={{
                                      fontWeight: subActive ? 500 : 400,
                                      fontSize: HELP_NAV_SUBMODULE_FONT_SIZE,
                                      lineHeight: 1.35,
                                      color: HELP_DOC_THEME.text,
                                      fontFamily: HELP_NAV_FONT_FAMILY,
                                      noWrap: true,
                                      sx: {
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                      },
                                    }}
                                  />
                                  <ChevronRightIcon sx={{ fontSize: 16, color: HELP_DOC_THEME.textMuted }} />
                                </ListItemButton>
                              </Box>
                            );
                          })}
                        </List>
                      )}
                    </Box>
                  </Collapse>
                </Box>
              );
            })}
          </List>
        )}
      </Box>
    </Box>
  );
};

export default HelpSidebar;
